import React, { useState, FC, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { Flex, Box, useToastContext, Spinner } from '@resideo/blueprint-react';
import { Formik } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  getCountryCodeForRegionCode,
  parsePhoneNumber,
} from 'awesome-phonenumber';
import config, {
  CONSUMER_TERMS_SUPPORTED_LANGUAGES,
  CONSUMER_EULA_SUPPORTED_LANGUAGES,
  PRO_TERMS_NA_SUPPORTED_LANGUAGES,
  PRO_TERMS_EMEA_SUPPORTED_LANGUAGES,
  PRO_PORTAL_URL,
  RECAPTCHA_SITE_KEY,
  STACK_IDENTIFIER,
} from 'config';
import { useSignUpContext } from 'routes/SignUp/context';
import { useNavigate } from 'react-router-dom';
import { WebAuth } from 'utils/auth/WebAuth';
import {
  signupUser,
  SignupApiError,
  buildSignupPayload,
} from 'utils/auth/signupApi';
import { useSignupErrorHandlers } from './useSignupErrorHandlers';
import { UserTypeEnum } from 'types/enums/User';
import useReturnToUrl from 'hooks/useReturnToUrl';
import { useAuth } from 'utils/auth';
import { useFeatureFlags } from 'context/FeatureFlags';
import { SFTY_WORKFLOW } from 'config/flags';
import { SignUpFormProps, SignUpValues } from './types';
import {
  createValidationFunction,
  updatePasswordValidator,
} from './validation';
import { getTermsUrls } from 'utils/cultureUtils';
import {
  FormContainer,
  NextButton,
  NavLink,
  RecaptchaErrorMessage,
} from './SignUpForm.styles';
import { SignUpFormFields } from './SignUpFormFields';
import { TermsSection } from './TermsSection';

// react-google-recaptcha loads the Enterprise script (enterprise.js) and uses
// grecaptcha.enterprise when this global flag is set. Required so the checkbox
// widget emits an Enterprise token that the RIS public signup API can verify
// via the reCAPTCHA Enterprise Assessment API.
// Guarded for non-browser runtimes (unit tests / SSR / build tooling) where
// `window` is undefined.
if (typeof window !== 'undefined') {
  (
    window as unknown as { recaptchaOptions?: { enterprise?: boolean } }
  ).recaptchaOptions = {
    enterprise: true,
  };
}

const SignUpForm: FC<SignUpFormProps> = ({
  email,
  phoneNumber,
  userType,
  inviteToken,
  signUpApp,
  signUpPartnerGuest,
  isInAppWebview,
}): JSX.Element => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);
  const [phoneCountry, setPhoneCountry] = useState<string>(() => {
    if (!phoneNumber) return 'us';
    const pn = parsePhoneNumber(phoneNumber.trim());
    return pn.regionCode?.toLowerCase() ?? 'us';
  });
  const [passwordValidator, setPasswordValidator] = useState({
    lowercase: false,
    uppercase: false,
    number: false,
    symbol: false,
    length: false,
    match: false,
  });

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const webAuth = useMemo(() => new WebAuth(), []);
  const { addToast } = useToastContext();
  const { addSignUpValues } = useSignUpContext();
  const { SIGNUP_RECAPTCHA } = useFeatureFlags();
  const [submitting, setSubmitting] = useState(false);
  const [auth0Error, setAuth0Error] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  // Submit-level error shown inline below the reCAPTCHA widget (never a toast
  // and never tied to the email field, to avoid revealing account existence).
  const [signupError, setSignupError] = useState<string | null>(null);

  // When enabled, signup goes through the RIS public API (reCAPTCHA-protected).
  // When disabled, we fall back to the legacy Auth0 /dbconnections/signup flow.
  const recaptchaEnabled = SIGNUP_RECAPTCHA && !!RECAPTCHA_SITE_KEY;

  const { getReturnToUrl, deleteUrlFromStorage, SignInURL } = useReturnToUrl();
  const { loginWithRedirect } = useAuth();

  const handleSignInWithInvite = (e: React.MouseEvent, userEmail: string) => {
    e.preventDefault();
    loginWithRedirect({
      isNewUser: false,
      email: userEmail,
      appState: {
        returnTo: `${PRO_PORTAL_URL}/sign-up/accept-invite?invite=${inviteToken}`,
      },
    });
  };

  const redirectAfterAccountCreated = (userEmail: string | undefined) => {
    deleteUrlFromStorage();

    if (inviteToken && userType === UserTypeEnum.PARTNER) {
      navigate(`/sign-up/success-invite?invite=${inviteToken}`);
    } else {
      addSignUpValues({ email: userEmail, userType });
      navigate('/sign-up/success');
    }
  };

  // Clears the single-use reCAPTCHA token and resets the widget so the user
  // must solve a fresh challenge before the next submit attempt.
  const resetRecaptcha = () => {
    recaptchaRef.current?.reset();
    setRecaptchaToken(null);
  };

  const signInErrorLink = (userEmail?: string) => (
    <NavLink
      data-test-sign-in-link
      href={inviteToken ? '#' : SignInURL()}
      onClick={
        inviteToken && userEmail
          ? e => handleSignInWithInvite(e, userEmail)
          : undefined
      }>
      {t('common.signIn')}
    </NavLink>
  );

  const { handleAuth0Error, handleRisError } = useSignupErrorHandlers({
    addToast,
    setSignupError,
    signInErrorLink,
  });

  // Signs up via the RIS public API using the current reCAPTCHA token. Any
  // failure invalidates the single-use token, so we reset the widget to force a
  // fresh challenge before the user can retry. A 404 means the backend endpoint
  // is not yet available, so we fall back to the legacy Auth0 flow silently.
  const submitViaRis = async (
    values: SignUpValues,
    setFieldError: (field: string, message: any) => void,
    token: string
  ) => {
    try {
      const payload = buildSignupPayload({
        values,
        userType,
        stackIdentifier: STACK_IDENTIFIER,
        inviteToken,
        returnToUrl: getReturnToUrl(),
        signUpPartnerGuest,
        signUpApp,
        isInAppWebview,
      });
      const result = await signupUser(payload, token, config.clientId);
      redirectAfterAccountCreated(result?.data?.email || values.email);
    } catch (err) {
      if (err instanceof SignupApiError && err.kind === 'not_found') {
        Sentry.addBreadcrumb({
          message: 'RIS Signup endpoint not found; falling back to Auth0',
          level: 'warning',
        });
        await submitViaAuth0(values, setFieldError);
        return;
      }
      handleRisError(err, setFieldError, values.email);
      setSubmitting(false);
      resetRecaptcha();
    }
  };

  const submitViaAuth0 = async (
    values: SignUpValues,
    setFieldError: (field: string, message: any) => void
  ) => {
    try {
      const auth0Response = await webAuth.signUp({
        email: values.email,
        password: values.password,
        name: `${values.firstName} ${values.lastName}`,
        metadata: {
          profileAttributes: JSON.stringify({
            firstName: values.firstName,
            lastName: values.lastName,
            locale: window.navigator.language,
            phoneNumber: values.phoneNumber || '',
            countryCode: values.country,
          }),
          inviteId: inviteToken || '',
          signUpUserType: userType,
          returnToUrl: getReturnToUrl(),
          signUpPartnerGuest: (signUpPartnerGuest as string) || '',
          signUpApp: (signUpApp as string) || '',
          stackIdentifier: STACK_IDENTIFIER,
          ...(isInAppWebview && { isInAppWebview: 'true' }),
        },
      });

      redirectAfterAccountCreated(auth0Response?.email || values.email);
    } catch (e) {
      handleAuth0Error(e, setFieldError, values.email);
      setAuth0Error((e as any)?.message);
      setSubmitting(false);
    }
  };

  const handleSubmit = async (values: SignUpValues, { setFieldError }) => {
    setAuth0Error(null);
    setSignupError(null);

    if (recaptchaEnabled && !recaptchaToken) {
      setSignupError(t('user.signUp.recaptchaRequired'));
      return;
    }

    const validUserTypes = Object.values(UserTypeEnum) as string[];
    if (!userType || !validUserTypes.includes(userType)) {
      Sentry.captureMessage(
        `Signup Failed - signUpUserType is invalid: ${userType}`,
        'error'
      );
      addToast({
        toastType: 'Error',
        message: t('user.signUp.genericError'),
      });
      return;
    }

    setSubmitting(true);

    Sentry.addBreadcrumb({
      message: 'Signup Form Submitted',
      level: 'debug',
      data: { returnToUrl: getReturnToUrl() },
    });

    if (recaptchaEnabled) {
      await submitViaRis(values, setFieldError, recaptchaToken as string);
      return;
    }

    await submitViaAuth0(values, setFieldError);
  };

  const validate = (values: SignUpValues) => {
    const validator = updatePasswordValidator(
      values.password,
      values.passwordConfirmation
    );
    setPasswordValidator(prev =>
      prev.lowercase === validator.lowercase &&
      prev.uppercase === validator.uppercase &&
      prev.number === validator.number &&
      prev.symbol === validator.symbol &&
      prev.length === validator.length &&
      prev.match === validator.match
        ? prev
        : validator
    );

    const validateFn = createValidationFunction(userType, validator, t);
    return validateFn(values);
  };

  const initialValues: SignUpValues = {
    firstName: '',
    lastName: '',
    email: email || '',
    phoneNumber: (() => {
      if (!phoneNumber) return '';
      const pn = parsePhoneNumber(phoneNumber.trim());
      return pn.valid ? (pn.number?.international ?? phoneNumber) : phoneNumber;
    })(),
    country: (() => {
      if (!phoneNumber) return '';
      const pn = parsePhoneNumber(phoneNumber.trim());
      return pn.regionCode ?? '';
    })(),
    password: '',
    passwordConfirmation: '',
    termsAccepted: false,
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validate}>
      {({
        dirty,
        values,
        errors,
        isValid,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        touched,
      }) => {
        const isCountrySelected = !!(values?.country && values.country !== '');

        const {
          proTermsUrl,
          consumerTermsUrl,
          consumerEulaUrl,
          privacyPolicyUrl,
        } = getTermsUrls(values.country || '', i18n.languages, {
          consumerTerms: CONSUMER_TERMS_SUPPORTED_LANGUAGES,
          consumerEula: CONSUMER_EULA_SUPPORTED_LANGUAGES,
          proTermsNA: PRO_TERMS_NA_SUPPORTED_LANGUAGES,
          proTermsEMEA: PRO_TERMS_EMEA_SUPPORTED_LANGUAGES,
        });

        return (
          <FormContainer
            data-test-registration-form
            autoComplete='off'
            noValidate>
            <input name='invited' type='hidden' />

            <SignUpFormFields
              email={email}
              isPhonePrePopulated={(() => {
                if (!SFTY_WORKFLOW || !phoneNumber) return false;
                const pn = parsePhoneNumber(phoneNumber.trim());
                return pn.valid && !!pn.regionCode;
              })()}
              userType={userType}
              showPassword={showPassword}
              showPasswordConfirmation={showPasswordConfirmation}
              onTogglePassword={() => setShowPassword(!showPassword)}
              onTogglePasswordConfirmation={() =>
                setShowPasswordConfirmation(!showPasswordConfirmation)
              }
              passwordValidator={passwordValidator}
              countryValue={values.country}
              phoneCountry={phoneCountry}
              onCountryChange={val => {
                setFieldValue('country', val);
                const dialCode = getCountryCodeForRegionCode(val);
                const phoneDigits = (values.phoneNumber || '').replaceAll(
                  /\D/g,
                  ''
                );
                const sameDialCode =
                  !!dialCode &&
                  !!phoneDigits &&
                  phoneDigits.startsWith(String(dialCode));
                if (!sameDialCode) {
                  // Different dial code: shift the phone flag and clear the value.
                  setPhoneCountry(val.toLowerCase());
                  if (phoneDigits) {
                    setFieldValue('phoneNumber', '');
                  }
                }
                // Same dial code: phoneCountry stays unchanged, so the country
                // prop passed to react-phone-input-2 never changes and the
                // pre-filled number is preserved.
              }}
              onCountryBlur={() => setFieldTouched('country')}
              countryError={errors.country}
              countryTouched={touched.country}
              passwordConfirmationError={errors.passwordConfirmation}
              passwordConfirmationTouched={touched.passwordConfirmation}
            />

            <TermsSection
              isCountrySelected={isCountrySelected}
              termsAccepted={values.termsAccepted}
              onTermsChange={checked => setFieldValue('termsAccepted', checked)}
              onFieldTouched={setFieldTouched}
              userType={userType}
              country={values.country}
              proTermsUrl={proTermsUrl}
              consumerTermsUrl={consumerTermsUrl}
              consumerEulaUrl={consumerEulaUrl}
              privacyPolicyUrl={privacyPolicyUrl}
            />

            {recaptchaEnabled && (
              <Flex
                paddingTop='large'
                paddingBottom={signupError ? 'small' : 'medium'}
                flexDirection='column'
                alignItems='flex-start'
                data-test-recaptcha>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={token => {
                    setRecaptchaToken(token);
                    setSignupError(null);
                  }}
                  onExpired={() => setRecaptchaToken(null)}
                  onErrored={() => setRecaptchaToken(null)}
                />
                {signupError && (
                  <RecaptchaErrorMessage role='alert' data-test-signup-error>
                    {signupError}
                  </RecaptchaErrorMessage>
                )}
              </Flex>
            )}

            <Box>
              <Flex padding='large' justifyContent='center'>
                <NextButton
                  className='common-button primary'
                  variant='primary'
                  type='submit'
                  disabled={
                    !dirty ||
                    !isValid ||
                    isSubmitting ||
                    (recaptchaEnabled && !recaptchaToken)
                  }
                  data-test-create-password-submit-button>
                  {submitting && !auth0Error && (
                    <Spinner
                      aria-label={t('common.saving')}
                      marginX='small'
                      color='white'
                      verticalAlign='text-top'
                    />
                  )}
                  {(!submitting || auth0Error) && t('common.next')}
                </NextButton>
              </Flex>
            </Box>
          </FormContainer>
        );
      }}
    </Formik>
  );
};

export default SignUpForm;
